import { CONSENT_EVENT, initializeGoogleTag } from "@/lib/googleTag";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { clearAnalyticsStorage } from "@/lib/analytics";
import "./CookieConsent.css";

const CONSENT_KEY = "analytics_consent";
const OPEN_EVENT = "sporgmig:open-cookie-settings";
const readChoices = () => ({
  statistics: localStorage.getItem(CONSENT_KEY) === "granted",
  marketing: localStorage.getItem("ads_consent") === "granted",
});

function CookieCategory({ label, checked, onChange, required = false }) {
  return <div className="cookie-category">
    <span>{label}</span>
    <button type="button" role="switch" aria-label={label} aria-checked={checked}
      disabled={required} title={required ? "Altid aktive" : undefined}
      className="cookie-switch" onClick={onChange}>
      <span aria-hidden="true"/>
    </button>
  </div>;
}

export default function CookieConsent() {
  const [choices, setChoices] = useState(readChoices);
  const [open, setOpen] = useState(() => !localStorage.getItem(CONSENT_KEY));
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const showSettings = () => {
      setChoices(readChoices());
      setDetailsOpen(false);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, showSettings);
    return () => window.removeEventListener(OPEN_EVENT, showSettings);
  }, []);

  const choose = (selection) => {
    localStorage.setItem(CONSENT_KEY, selection.statistics ? "granted" : "denied");
    localStorage.setItem("ads_consent", selection.marketing ? "granted" : "denied");
    setChoices(selection);
    if (!selection.statistics) clearAnalyticsStorage();
    // Optional measurement must never prevent saving or dismissing the banner.
    try { initializeGoogleTag(); } catch { /* A blocked tag does not undo consent. */ }
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-labelledby="cookie-title" aria-describedby="cookie-description">
      <div className="cookie-banner__inner">
        <div className="cookie-banner__content">
          <div className="cookie-banner__heading">
            <Cookie size={22} aria-hidden="true"/>
            <h2 id="cookie-title">Du bestemmer over dine cookies</h2>
          </div>
          <p id="cookie-description">Vi bruger nødvendige cookies for at få SpørgMig til at fungere. Med din tilladelse bruger vi også cookies til statistik og til at måle effekten af vores annoncer. Du vælger selv, hvad du vil tillade, og kan altid ændre dit valg i cookieindstillingerne.</p>
          <div className="cookie-banner__categories">
            <CookieCategory label="Nødvendige" checked required/>
            <CookieCategory label="Statistik" checked={choices.statistics} onChange={() => setChoices({ ...choices, statistics: !choices.statistics })}/>
            <CookieCategory label="Marketing" checked={choices.marketing} onChange={() => setChoices({ ...choices, marketing: !choices.marketing })}/>
            <button type="button" className="cookie-details-toggle" aria-expanded={detailsOpen} aria-controls="cookie-details" onClick={() => setDetailsOpen(!detailsOpen)}>{detailsOpen ? "Skjul detaljer" : "Vis detaljer"}<span aria-hidden="true">{detailsOpen ? "−" : "+"}</span></button>
          </div>
          {detailsOpen && <div id="cookie-details" className="cookie-banner__details">
            <p><strong>Nødvendige — altid aktive.</strong> Bruges til login, sikker drift og til at huske dine cookievalg.</p>
            <p><strong>Statistik.</strong> Hjælper os med at forstå, hvordan siden bliver brugt, så vi kan forbedre den. Omfatter Google Analytics, når det er aktiveret.</p>
            <p><strong>Marketing.</strong> Bruges til at måle, om vores annoncer fører til forespørgsler, via Google Ads. Vi bruger ikke denne måling til at tilpasse annoncer til dig.</p>
            <Link to="/cookies">Læs vores cookiepolitik</Link>
          </div>}
        </div>
        <div className="cookie-banner__actions">
          <button type="button" className="cookie-button cookie-button--primary" onClick={() => choose({ statistics: true, marketing: true })} data-testid="accept-analytics">Tillad alle</button>
          <button type="button" className="cookie-button" onClick={() => choose(choices)} data-testid="accept-selected-cookies">Tillad valgte</button>
          <button type="button" className="cookie-button" onClick={() => choose({ statistics: false, marketing: false })} data-testid="decline-analytics">Afvis</button>
        </div>
      </div>
    </div>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}
